import { DeleteGroups } from '@/backoffice/features/groups/components/delete-groups/delete-groups';
import { useBackofficeGroupResourceUpdatePromises } from '@/backoffice/features/groups/hooks/use-backoffice-group-resource-update-promises';
import { useBackofficeResourceElements } from '@/backoffice/features/groups/hooks/use-backoffice-resource-elements';
import {
  useCreateBackofficeResourceGroupMutation,
  useLazyReadBackofficeResourceGroupNameByIdQuery,
  useReadAllBackofficeResourceGroupsQuery,
  useReadBackofficeResourceGroupsForSelectQuery,
  useReadBackofficeResourceGroupByIdQuery,
  useReadBackofficeResourceGroupNameByIdQuery,
  useReadBackofficeResourceGroupNamesForRoleRowQuery,
  useReadSubGroupsForBackofficeResourceGroupQuery,
  useUpdateBackofficeResourceGroupMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { GroupResourceBatchActions } from '@/shared/features/groups/components/group-resource-batch-actions/group-resource-batch-actions';
import { GroupedItemContextMenuRenderer } from '@/shared/features/groups/components/grouped-item-context-menu/grouped-item-context-menu.renderer';
import {
  TGroupResourceItem,
  TGroupsApi,
  TGroupsConfig,
} from '@/shared/features/groups/context/groups-config-context.types';

// satisfies guards key completeness; the cast bridges the __typename mismatch.
const api = {
  useReadAllResourceGroupsQuery: useReadAllBackofficeResourceGroupsQuery,
  useReadResourceGroupsForSelectQuery:
    useReadBackofficeResourceGroupsForSelectQuery,
  useReadResourceGroupByIdQuery: useReadBackofficeResourceGroupByIdQuery,
  useReadResourceGroupNameByIdQuery:
    useReadBackofficeResourceGroupNameByIdQuery,
  useLazyReadResourceGroupNameByIdQuery:
    useLazyReadBackofficeResourceGroupNameByIdQuery,
  useReadResourceGroupNamesForRoleRowQuery:
    useReadBackofficeResourceGroupNamesForRoleRowQuery,
  useReadSubGroupsForResourceGroupQuery:
    useReadSubGroupsForBackofficeResourceGroupQuery,
  useCreateResourceGroupMutation: useCreateBackofficeResourceGroupMutation,
  useUpdateResourceGroupMutation: useUpdateBackofficeResourceGroupMutation,
} satisfies Record<keyof TGroupsApi, unknown>;

const renderBatchActions = (selectedRaw: unknown[], onSuccess: () => void) => (
  <GroupResourceBatchActions
    selectedItems={selectedRaw as TGroupResourceItem[]}
    onSuccess={onSuccess}
  />
);

export const backofficeGroupsConfig: TGroupsConfig = {
  groupRoutePath: '/groups',
  permissionActions: {
    createGroup: 'backofficeGroup:createGroup',
    readGroup: 'backofficeGroup:readGroup',
    updateGroup: 'backofficeGroup:updateGroup',
    deleteGroup: 'backofficeGroup:deleteGroup',
  },
  api: api as unknown as TGroupsApi,
  useResourceUpdatePromises: useBackofficeGroupResourceUpdatePromises,
  useResourceElements: useBackofficeResourceElements,
  // Move/remove actions for the user and role tabs; links point at the
  // Backoffice detail routes rather than the group-scoped Console paths.
  resourceLists: {
    users: {
      rowActions: GroupedItemContextMenuRenderer,
      renderBatchActions,
      getItemLink: (_groupId, itemId) => `/users/${itemId}`,
    },
    roles: {
      rowActions: GroupedItemContextMenuRenderer,
      renderBatchActions,
      getItemLink: (_groupId, itemId) => `/roles/${itemId}`,
    },
    subGroups: {
      renderBatchActions: (selectedRaw, onSuccess) => (
        <>
          <GroupResourceBatchActions
            selectedItems={selectedRaw as TGroupResourceItem[]}
            onSuccess={onSuccess}
          />
          <DeleteGroups
            groupIds={(selectedRaw as { groupId: string }[]).map(
              (group) => group.groupId,
            )}
            onSuccess={onSuccess}
          />
        </>
      ),
    },
  },
};
